import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { trigger, state, style } from '@angular/animations';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';

import { DatatableBaseComponent } from '@components/datatable-base.component';
import { UiService } from '@services/ui.service';
import { AddressGroupsService } from '@services/address-groups.service';
import { Address } from '@classes/address';
import { AddressGroup } from '@classes/address-group';
import { Site } from '@classes/site';
import { SiteGroup } from '@classes/site-group';

type LinkedSite = {
  name: string;
  type: 'site' | 'siteGroup';
};

@Component({
  selector: 'app-address-groups',
  templateUrl: './address-groups.component.html',
  styleUrls: ['./address-groups.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
    ]),
  ],
})
export class AddressGroupsComponent
  extends DatatableBaseComponent
  implements OnInit, AfterViewInit
{
  addressGroups: AddressGroup[];
  addresses: Address[] = [];
  sites: Site[] = [];
  siteGroups: SiteGroup[] = [];

  readonly maxDisplayedItems = 4;
  private expandedAddressGroupIds: string[] = [];
  private expandedLinkedSiteGroupIds: string[] = [];

  displayedColumns = ['name', 'addresses', 'sites', 'actions'];
  dataSource: MatTableDataSource<AddressGroup>;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    protected uiSrv: UiService,
    private route: ActivatedRoute,
    private addressGroupsSrv: AddressGroupsService,
  ) {
    super(uiSrv);
  }

  ngOnInit() {
    super.ngOnInit();
    this.getRouteData();
    this.initSubscriptions();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  getVisibleAddresses(group: AddressGroup): Address[] {
    return this.isAddressesExpanded(group)
      ? group.addresses || []
      : (group.addresses || []).slice(0, this.maxDisplayedItems);
  }

  getVisibleLinkedSites(group: AddressGroup): LinkedSite[] {
    const linkedSites = this.getLinkedSites(group);
    return this.isLinkedSitesExpanded(group)
      ? linkedSites
      : linkedSites.slice(0, this.maxDisplayedItems);
  }

  getRemainingAddressesCount(group: AddressGroup): number {
    return Math.max((group.addresses || []).length - this.maxDisplayedItems, 0);
  }

  getRemainingLinkedSitesCount(group: AddressGroup): number {
    return Math.max(this.getLinkedSites(group).length - this.maxDisplayedItems, 0);
  }

  isAddressesExpanded(group: AddressGroup): boolean {
    return this.expandedAddressGroupIds.includes(group.id);
  }

  isLinkedSitesExpanded(group: AddressGroup): boolean {
    return this.expandedLinkedSiteGroupIds.includes(group.id);
  }

  toggleAddresses(group: AddressGroup): void {
    this.toggleItem(this.expandedAddressGroupIds, group.id);
  }

  toggleLinkedSites(group: AddressGroup): void {
    this.toggleItem(this.expandedLinkedSiteGroupIds, group.id);
  }

  delete(idAddressGroup: string) {
    const confirmation = confirm(
      "Êtes-vous certain de vouloir supprimer ce groupe d'adresses ?",
    );

    if (confirmation) {
      this.addressGroupsSrv.delete(idAddressGroup).subscribe(() => {
        this.uiSrv.showSnackbar('repository.addressGroups.list.deleted', true);
      });
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  private initTable(): void {
    this.dataSource = new MatTableDataSource(this.addressGroups);
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'addresses') {
        return (item.addresses || []).map((address) => address.name).join(', ');
      }
      if (property === 'sites') {
        return [...(item.sites || []), ...(item.siteGroups || [])]
          .map((itemLink) => itemLink.name)
          .join(', ');
      }
      return item[property];
    };
    this.dataSource.filterPredicate = (data, filter: string) => {
      const dataStr = [
        data.name,
        ...(data.addresses || []).map((address) => address.name),
        ...(data.sites || []).map((site) => site.name),
        ...(data.siteGroups || []).map((siteGroup) => siteGroup.name),
      ]
        .join(' ')
        .toLowerCase();
      return dataStr.includes(filter.trim().toLowerCase());
    };
    this.dataSource.sort = this.sort;
  }

  private getRouteData(): void {
    this.route.data.subscribe(
      (data: {
        addressGroups: AddressGroup[];
        addresses: Address[];
        sites: Site[];
        siteGroups: SiteGroup[];
      }) => {
        this.addressGroups = data.addressGroups || [];
        this.addresses = data.addresses || [];
        this.sites = data.sites || [];
        this.siteGroups = data.siteGroups || [];
        this.initTable();
      },
    );
  }

  private initSubscriptions(): void {
    this.addSubscription(
      this.addressGroupsSrv.addressGroupsListChanged.subscribe((addressGroups) => {
        this.dataSource.data = addressGroups || [];
        this.dataSource.sort = this.sort;
        this.onEditFinished();
      }),
    );
  }

  private toggleItem(items: string[], id: string): void {
    const index = items.indexOf(id);

    if (index === -1) {
      items.push(id);
    } else {
      items.splice(index, 1);
    }
  }

  private getLinkedSites(group: AddressGroup): LinkedSite[] {
    return [
      ...(group.sites || []).map((site) => ({
        name: site.name,
        type: 'site' as const,
      })),
      ...(group.siteGroups || []).map((siteGroup) => ({
        name: siteGroup.name,
        type: 'siteGroup' as const,
      })),
    ];
  }
}
